/**
 * FoxSell Fixed Bundle Carousel
 */

class FoxSellCarousel {
  constructor(element) {
    this.carousel = element;
    this.track = this.carousel.querySelector('.foxsell-carousel-track');
    this.prevButton = this.carousel.parentElement.querySelector('[data-carousel-prev]');
    this.nextButton = this.carousel.parentElement.querySelector('[data-carousel-next]');
    this.cards = Array.from(this.track.children);
    
    this.currentIndex = 0;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    
    this.init();
  }

  init() {
    if (this.cards.length === 0) return;

    // Calculate visible cards
    this.updateVisibleCards();
    
    // Set up event listeners
    this.prevButton?.addEventListener('click', () => this.prev());
    this.nextButton?.addEventListener('click', () => this.next());
    
    // Add resize listener
    window.addEventListener('resize', () => this.handleResize());
    
    // Add touch/mouse drag support
    this.addDragSupport();
    
    // Initial button state
    this.updateButtons();
  }

  updateVisibleCards() {
    const containerWidth = this.carousel.offsetWidth;
    const cardWidth = this.cards[0]?.offsetWidth || 300;
    const gap = 16;
    
    // Calculate how many cards fit
    this.visibleCards = Math.floor(containerWidth / (cardWidth + gap));
    if (this.visibleCards < 1) this.visibleCards = 1;
  }

  getCardWidth() {
    const card = this.cards[0];
    if (!card) return 0;
    
    const cardWidth = card.offsetWidth;
    const gap = 16;
    return cardWidth + gap;
  }

  next() {
    const maxIndex = Math.max(0, this.cards.length - this.visibleCards);
    
    if (this.currentIndex < maxIndex) {
      this.currentIndex += 1;
      this.updatePosition();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
      this.updatePosition();
    }
  }

  updatePosition() {
    const cardWidth = this.getCardWidth();
    const translateX = -(this.currentIndex * cardWidth);
    
    this.track.style.transform = `translateX(${translateX}px)`;
    this.currentTranslate = translateX;
    this.prevTranslate = translateX;
    
    this.updateButtons();
  }

  updateButtons() {
    const maxIndex = Math.max(0, this.cards.length - this.visibleCards);
    
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex >= maxIndex;
    }
  }

  handleResize() {
    this.updateVisibleCards();
    
    // Adjust current index if needed
    const maxIndex = Math.max(0, this.cards.length - this.visibleCards);
    if (this.currentIndex > maxIndex) {
      this.currentIndex = maxIndex;
    }
    
    this.updatePosition();
  }

  addDragSupport() {
    // Touch events
    this.track.addEventListener('touchstart', (e) => this.dragStart(e.touches[0].clientX), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.drag(e.touches[0].clientX), { passive: true });
    this.track.addEventListener('touchend', () => this.dragEnd());

    // Mouse events
    this.track.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.dragStart(e.clientX);
    });
    this.track.addEventListener('mousemove', (e) => this.drag(e.clientX));
    this.track.addEventListener('mouseup', () => this.dragEnd());
    this.track.addEventListener('mouseleave', () => this.dragEnd());

    // Prevent image drag
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  dragStart(clientX) {
    this.isDragging = true;
    this.startPos = clientX;
    this.track.style.transition = 'none';
  }

  drag(clientX) {
    if (!this.isDragging) return;
    
    const currentPosition = clientX;
    const diff = currentPosition - this.startPos;
    this.currentTranslate = this.prevTranslate + diff;
    
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  dragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.track.style.transition = 'transform 0.4s ease';
    
    const movedBy = this.currentTranslate - this.prevTranslate;
    const cardWidth = this.getCardWidth();
    
    // Determine direction and threshold
    if (movedBy < -cardWidth / 4 && this.currentIndex < this.cards.length - this.visibleCards) {
      this.next();
    } else if (movedBy > cardWidth / 4 && this.currentIndex > 0) {
      this.prev();
    } else {
      this.updatePosition();
    }
  }
}

// Initialize all carousels on the page
function initFoxSellCarousels() {
  const carousels = document.querySelectorAll('[data-carousel]');
  carousels.forEach(carousel => new FoxSellCarousel(carousel));
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFoxSellCarousels);
} else {
  initFoxSellCarousels();
}
